<template>
  <div
    data-testid="public-template-2"
    class="bg-pale-stone text-charcoal-blue antialiased rms-t2-selection font-body rms-template-2-root"
    :style="wrapperStyle"
  >
    <Template2Header
      :name="restaurant.name"
      :viewer="restaurant.viewer"
      :owner-view-mode="ownerViewMode"
      :languages="languages"
      :current-locale="currentLocale"
      :reservation-available="restaurant.reservation_available === true"
      :slug="restaurant.slug || ''"
      :primary-color="restaurant.primary_color || null"
      :view-list-label="viewListLabel"
      :nav-label="navLabel"
      :view-list-aria-label="viewListAriaLabel"
      :reserve-button-label="reserveButtonLabel"
      :reserve-modal-heading="reserveModalHeading"
      :reserve-pax-label="reservePaxLabel"
      :feedback-available="restaurant.feedback_available === true"
      @select-locale="$emit('select-locale', $event)"
    />
    <main class="space-y-0">
      <Template2Hero
        :name="restaurant.name"
        :tagline="restaurant.tagline"
        :description="(restaurant.description || '').trim()"
        :logo-url="restaurant.logo_url || ''"
        :viewer="restaurant.viewer"
        :owner-view-mode="ownerViewMode"
        :banner-url="restaurant.banner_url"
        :year-established="restaurant.year_established"
        :operating-hours="effectiveOperatingHours"
        :view-list-label="viewListLabel"
        :has-about="hasAbout"
      />
      <div
        v-motion
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <Template2Menu
          :menu-groups="restaurant.menu_groups || []"
          :menu-items="restaurant.menu_items || []"
          :currency="restaurant.currency || 'USD'"
          :show-currency="restaurant.show_currency !== false"
          :section-title="menuSectionTitle"
          :empty-message="menuEmptyMessage"
          :show-owner-edit-fab="ownerViewMode"
          @select-item="onSelectMenuItem"
        />
      </div>
      <PublicMenuItemDetailModal
        v-model="detailOpen"
        v-model:selected-modifier-uuids="detailModifierUuids"
        :item="detailItem"
        :selected-sku="detailSelectedSku"
        :category-name="detailCategoryName"
        :primary-color="restaurant.primary_color"
        :currency="restaurant.currency || 'USD'"
        :show-currency="restaurant.show_currency !== false"
        :quick-order-available="!!injectedQuickOrderAvailable"
        :cart-item-quantity="detailCartQty"
        :business-type="restaurant.business_type || 'restaurants'"
        :require-variant-selection="detailRequiresVariantSelection"
        @update:model-value="onDetailClose"
        @update:selected-sku="onDetailSelectedSku"
        @add-to-cart="onAddToCart"
      />
      <template v-if="restaurant.feedback_available">
        <section
          v-if="hasPublicFeedbacks"
          id="reviews"
          v-motion
          class="border-y border-charcoal-blue"
          aria-label="Reviews"
          :initial="sectionMotion.initial"
          :enter="sectionMotion.enter"
        >
          <Template2Reviews :feedbacks="restaurant.feedbacks || []" />
        </section>
        <!-- Native wrapper carries #reviews when there are no cards (v-motion does not forward id to DOM). -->
        <div
          :id="hasPublicFeedbacks ? undefined : 'reviews'"
          :role="hasPublicFeedbacks ? undefined : 'region'"
          :aria-label="hasPublicFeedbacks ? undefined : 'Reviews'"
        >
          <div v-motion :initial="sectionMotion.initial" :enter="sectionMotion.enter">
            <slot name="feedback-form" />
          </div>
        </div>
      </template>
      <div
        v-else-if="restaurant.viewer?.is_owner && ownerViewMode"
        v-motion
        class="border-y border-charcoal-blue"
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <div class="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
          <div class="py-4 px-4 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5">
            <p class="text-sm font-mono text-charcoal-blue/70">{{ $t('public.noFeedbackAvailableOwnerMessage') }}</p>
          </div>
        </div>
      </div>
      <div
        v-motion
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enterAbout"
      >
        <Template2About
          :text="(restaurant.description || '').trim()"
          :viewer="restaurant.viewer"
          :owner-view-mode="ownerViewMode"
        />
      </div>
      <div
        v-motion
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enterContact"
      >
        <Template2Contact
          :contacts="restaurant.contacts || []"
          :operating-hours="effectiveOperatingHours"
        />
      </div>
      <PublicMailingListSection
        v-if="restaurant.mailing_list_available"
        :restaurant="restaurant"
        template="template-2"
      />
      <div
        v-else-if="restaurant.viewer?.is_owner && ownerViewMode"
        v-motion
        :initial="sectionMotion.initial"
        :enter="sectionMotion.enter"
      >
        <div class="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
          <div class="py-4 px-4 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5">
            <p class="text-sm font-mono text-charcoal-blue/70">{{ $t('public.noMailingListAvailableOwnerMessage') }}</p>
          </div>
        </div>
      </div>
      <Template2Map
          :restaurant="restaurant"
          :viewer="restaurant.viewer"
          :owner-view-mode="ownerViewMode"
        />
    </main>
    <div
      v-motion
      :initial="footerMotion.initial"
      :enter="footerMotion.enter"
    >
      <Template2Footer
        :restaurant-name="restaurant.name"
        :logo-url="restaurant.logo_url || ''"
        :viewer="restaurant.viewer"
        :owner-view-mode="ownerViewMode"
        :show-credits="!restaurant.hide_footer_credits"
        :modal-locale="restaurant.locale || restaurant.default_locale || 'en'"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, inject } from 'vue'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { DAY_KEYS, hasOperatingHoursSchedule } from '@/utils/availability'
import { restaurantHasMapOrLink } from '@/utils/googleMapsUrl'
import Template2Header from '@/components/public/templates/template-2/Template2Header.vue'
import Template2Hero from '@/components/public/templates/template-2/Template2Hero.vue'
import Template2Menu from '@/components/public/templates/template-2/Template2Menu.vue'
import Template2Reviews from '@/components/public/templates/template-2/Template2Reviews.vue'
import Template2About from '@/components/public/templates/template-2/Template2About.vue'
import Template2Contact from '@/components/public/templates/template-2/Template2Contact.vue'
import Template2Map from '@/components/public/templates/template-2/Template2Map.vue'
import Template2Footer from '@/components/public/templates/template-2/Template2Footer.vue'
import PublicMailingListSection from '@/components/public/PublicMailingListSection.vue'
import PublicMenuItemDetailModal from '@/components/public/PublicMenuItemDetailModal.vue'
import { buildQuickOrderCartLinePayload } from '@/utils/publicMenuCart.js'
import { useToastStore } from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import { useBusinessTypeLabels } from '@/composables/useBusinessTypeLabels'

const { t } = useI18n()
const toastStore = useToastStore()
const { sectionTitle: menuSectionTitle, emptyMessage: menuEmptyMessage, viewListLabel, navLabel, viewListAriaLabel, reserveButtonLabel, reserveModalHeading, reservePaxLabel } = useBusinessTypeLabels(() => props.restaurant, t)
const injectedCart = inject('quickOrderCart', ref(null))
const injectedQuickOrderAvailable = inject('quickOrderAvailable', ref(false))

const props = defineProps({
  restaurant: { type: Object, required: true },
  /** When false, owner-only UI (e.g. needs-data notice) is hidden. */
  ownerViewMode: { type: Boolean, default: true },
  languages: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
})

defineEmits(['select-locale'])

const DEFAULT_HOURS = Object.freeze(
  Object.fromEntries(DAY_KEYS.map(day => [day, { open: true, slots: [{ from: '09:00', to: '21:00' }] }]))
)

const effectiveOperatingHours = computed(() => {
  const hours = props.restaurant.operating_hours
  if (hours == null) return null
  return typeof hours === 'object' && Object.keys(hours).length > 0 ? hours : DEFAULT_HOURS
})

/** When false, skip the reviews grid; keep #reviews on the feedback form for in-page nav. */
const hasPublicFeedbacks = computed(() => (props.restaurant.feedbacks || []).length > 0)

const detailItem = ref(null)
const detailSelectedSku = ref(null)
const detailCategoryName = ref('Menu')
const detailOpen = ref(false)
const detailModifierUuids = ref([])
const detailRequiresVariantSelection = computed(
  () => detailItem.value?.type === 'with_variants' && detailSelectedSku.value == null,
)
let detailTriggerEl = null

function onSelectMenuItem(payload) {
  detailItem.value = payload?.item ?? null
  detailSelectedSku.value = payload?.sku ?? null
  detailCategoryName.value = payload?.categoryName ?? 'Menu'
  detailTriggerEl = payload?.trigger ?? null
  detailOpen.value = true
}

const detailCartQty = computed(() => {
  const c = injectedCart.value
  if (!c || !detailItem.value?.uuid) return 0
  return c.getItemQuantity(
    detailItem.value.uuid,
    detailSelectedSku.value?.uuid ?? null,
    detailModifierUuids.value,
  )
})

function onAddToCart({ item, quantity, selectedSku, modifier_uuids, replace }) {
  const c = injectedCart.value
  if (!c || !item) return
  const line = buildQuickOrderCartLinePayload({
    item,
    selectedSku,
    modifier_uuids: modifier_uuids ?? [],
  })
  const isUpdate = c.getItemQuantity(item.uuid, line.variant_uuid, line.modifier_uuids) > 0
  c.addItem({ ...line, quantity, replace: replace === true })
  toastStore.success(t(isUpdate ? 'public.quickOrder.itemUpdated' : 'public.quickOrder.itemAdded'))
}

function onDetailSelectedSku(sku) {
  detailSelectedSku.value = sku
}

function onDetailClose(open) {
  if (!open) {
    detailItem.value = null
    detailSelectedSku.value = null
    detailCategoryName.value = 'Menu'
    detailModifierUuids.value = []
    nextTick(() => {
      if (detailTriggerEl && typeof detailTriggerEl.focus === 'function') {
        detailTriggerEl.focus()
      }
      detailTriggerEl = null
    })
  }
}

const DEFAULT_PRIMARY = '#1152d4'

const reducedMotion = useReducedMotion()

const sectionMotion = computed(() => {
  if (reducedMotion.value) {
    return {
      initial: { opacity: 1, y: 0 },
      enter: { opacity: 1, y: 0, transition: { duration: 0 } },
      enterAbout: { opacity: 1, y: 0, transition: { duration: 0 } },
      enterContact: { opacity: 1, y: 0, transition: { duration: 0 } },
    }
  }
  return {
    initial: { opacity: 0, y: 20 },
    enter: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 80 } },
    enterAbout: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 100 } },
    enterContact: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 100 } },
  }
})

const footerMotion = computed(() => {
  if (reducedMotion.value) {
    return { initial: { opacity: 1 }, enter: { opacity: 1, transition: { duration: 0 } } }
  }
  return { initial: { opacity: 0 }, enter: { opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } } }
})

const hasOperatingHours = computed(() => hasOperatingHoursSchedule(effectiveOperatingHours.value))

const hasAbout = computed(() => ((props.restaurant?.description || '').trim().length > 0))

const hasMapOrLink = computed(() => restaurantHasMapOrLink(props.restaurant))

const wrapperStyle = computed(() => ({
  '--rms-public-primary': props.restaurant?.primary_color || DEFAULT_PRIMARY,
  '--t2-primary': props.restaurant?.primary_color || DEFAULT_PRIMARY,
  '--t2-text': '#1E293B',
  '--t2-border': '#1E293B',
  '--t2-bg': '#F3F4F6',
  '--t2-bg-elevated': '#ffffff',
  '--t2-radius': '4px',
  '--t2-error': '#dc2626',
  '--t2-success': '#16a34a',
}))
</script>
